import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-error';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type {
  EmailIntakeReconciliationItem,
  EmailIntakeReconciliationPage,
} from '@shared/email-intake-reconciliation';

interface Draft {
  decision: string;
  reason: string;
}

const emptyDraft: Draft = { decision: '', reason: '' };

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message.replace(/^\d{3}:\s*/, '');
  return 'The request failed. Please try again.';
}

function EvidenceValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span>Not recorded</span>;
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value !== 'object') return <span>{String(value)}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>None</span>;
    return (
      <ul className="list-disc pl-5">
        {value.map((entry, index) => <li key={index}><EvidenceValue value={entry} /></li>)}
      </ul>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return <span>None recorded</span>;
  return (
    <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-3 gap-y-1">
      {entries.map(([key, entry]) => (
        <div className="contents" key={key}>
          <dt className="font-medium text-gray-300">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}:</dt>
          <dd><EvidenceValue value={entry} /></dd>
        </div>
      ))}
    </dl>
  );
}

export function HistoricalEmailReconciliation({ account }: {
  account: { id: string; name: string };
}) {
  const [open, setOpen] = useState(false);
  const [cursorHistory, setCursorHistory] = useState<Array<number | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const cursor = cursorHistory[pageIndex];
  const baseUrl = `/api/admin/email-intake/${encodeURIComponent(account.id)}/reconciliation`;

  const page = useQuery<EmailIntakeReconciliationPage>({
    queryKey: [baseUrl, { after: cursor }],
    enabled: open,
    queryFn: async () => {
      const suffix = cursor === null ? '' : `?after=${encodeURIComponent(String(cursor))}`;
      return (await apiRequest('GET', `${baseUrl}${suffix}`)).json();
    },
    retry: 1,
  });

  useEffect(() => {
    setCursorHistory([null]);
    setPageIndex(0);
    setDrafts({});
    setNotice(null);
    setSubmissionError(null);
  }, [account.id]);

  const submit = useMutation({
    mutationFn: async ({ item, draft }: { item: EmailIntakeReconciliationItem; draft: Draft }) => {
      await apiRequest('POST', `${baseUrl}/${item.cacheId}`, {
        decision: draft.decision,
        snapshotToken: item.snapshotToken,
        reason: draft.reason.trim(),
      });
      return item;
    },
    onSuccess: async item => {
      setSubmissionError(null);
      setNotice(`Email cache #${item.cacheId} was reviewed successfully.`);
      setDrafts(current => ({ ...current, [item.cacheId]: emptyDraft }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/admin/email-intake'] }),
        queryClient.invalidateQueries({ queryKey: [baseUrl] }),
      ]);
    },
    onError: async (error, variables) => {
      setNotice(null);
      if (error instanceof ApiError && error.status === 409) {
        setDrafts(current => ({ ...current, [variables.item.cacheId]: emptyDraft }));
        setSubmissionError(
          'This record changed while you were reviewing it. The evidence was reloaded; choose a decision and enter a reason again.',
        );
        await queryClient.invalidateQueries({ queryKey: [baseUrl] });
        await page.refetch();
        return;
      }
      setSubmissionError(errorMessage(error));
    },
  });

  const updateDraft = (cacheId: number, change: Partial<Draft>) => {
    setNotice(null);
    setSubmissionError(null);
    setDrafts(current => ({
      ...current,
      [cacheId]: { ...(current[cacheId] ?? emptyDraft), ...change },
    }));
  };

  const showNext = () => {
    if (page.data?.nextCursor === null || page.data?.nextCursor === undefined) return;
    const nextIndex = pageIndex + 1;
    setCursorHistory(current => {
      const copy = current.slice(0, nextIndex);
      copy[nextIndex] = page.data!.nextCursor;
      return copy;
    });
    setPageIndex(nextIndex);
    setNotice(null);
    setSubmissionError(null);
  };

  return (
    <div className="rounded border border-gray-700 p-2">
      <button
        type="button"
        className="underline text-gray-200"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        Review historical blocked emails
      </button>
      <span className="text-gray-400"> — {account.name}</span>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-gray-400">
            This review does not send email or automatically replay intake. Existing safety
            restrictions remain in place; only the explicit decision submitted below is applied.
          </p>
          {page.isLoading && <p>Loading blocked emails…</p>}
          {page.isError && (
            <p role="alert" className="text-red-400">
              Could not load blocked emails: {errorMessage(page.error)}{' '}
              <button type="button" className="underline" onClick={() => void page.refetch()}>Retry</button>
            </p>
          )}
          {submissionError && <p role="alert" className="text-red-400">{submissionError}</p>}
          {notice && <p role="status" className="text-green-400">{notice}</p>}
          {page.data?.items.length === 0 && <p className="text-gray-400">No blocked emails on this page.</p>}
          {page.data?.items.map(item => {
            const draft = drafts[item.cacheId] ?? emptyDraft;
            const reasonLength = draft.reason.trim().length;
            const reasonIsValid = reasonLength >= 3 && reasonLength <= 2000;
            const isSubmitting = submit.isPending && submit.variables?.item.cacheId === item.cacheId;
            return (
              <article key={item.cacheId} className="rounded border border-gray-600 p-3 space-y-2">
                <h4 className="font-semibold text-gray-100">{item.subject || '(No subject)'}</h4>
                <p>From: {item.fromAddress || 'Unknown'} · UID {item.uid} · cache #{item.cacheId}</p>
                <p>Pending since: {item.pendingSince}</p>
                <details>
                  <summary className="cursor-pointer font-medium">Assignment, ignore, and notification evidence</summary>
                  <div className="mt-2 rounded bg-gray-800 p-2 text-gray-300">
                    <EvidenceValue value={item.evidence} />
                  </div>
                </details>
                <label className="block">
                  <span className="block font-medium">Safe decision</span>
                  <select
                    className="mt-1 rounded border border-gray-600 bg-gray-800 p-1 text-gray-100"
                    aria-label={`Safe decision for cache ${item.cacheId}`}
                    value={draft.decision}
                    onChange={event => updateDraft(item.cacheId, { decision: event.target.value })}
                  >
                    <option value="">Choose a decision…</option>
                    {item.allowedDecisions.map(decision => (
                      <option key={decision.value} value={decision.value}>{decision.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block font-medium">Review reason</span>
                  <textarea
                    className="mt-1 w-full rounded border border-gray-600 bg-gray-800 p-2 text-gray-100"
                    aria-label={`Review reason for cache ${item.cacheId}`}
                    required
                    minLength={3}
                    maxLength={2000}
                    value={draft.reason}
                    onChange={event => updateDraft(item.cacheId, { reason: event.target.value })}
                  />
                  <span className="block text-gray-400">3–2000 characters required.</span>
                </label>
                <button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1 text-white disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!draft.decision || !reasonIsValid || isSubmitting}
                  onClick={() => submit.mutate({ item, draft })}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit reviewed decision'}
                </button>
              </article>
            );
          })}
          {page.data && (
            <nav className="flex gap-3" aria-label="Blocked email pages">
              <button
                type="button"
                className="underline disabled:opacity-50"
                disabled={pageIndex === 0 || page.isFetching}
                onClick={() => setPageIndex(index => index - 1)}
              >
                Back
              </button>
              <button
                type="button"
                className="underline disabled:opacity-50"
                disabled={page.data.nextCursor === null || page.isFetching}
                onClick={showNext}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}